import React, { useState, useEffect, useCallback } from 'react';
import { Search, Filter, CheckSquare, ChevronDown, ChevronUp, FileText, Download } from 'lucide-react';

// Filter System as a separate component - Firebase compatible with WhatsApp Number Filter
const FilterSystem = ({ 
  data, 
  onFilteredDataChange, 
  filterOptions = {},
  showExport = true
}) => {
  // Default filter config that can be extended with props
  const options = {
    searchFields: ['customerName', 'whatsappNumber', 'email', 'localNumber', 'licenseNumber'],
    filterFields: {
      customerName: { type: 'text', label: 'Customer Name' },
      whatsappNumber: { type: 'text', label: 'WhatsApp Number' },
      licenseNumber: { type: 'text', label: 'License Number' },
      bikeModel: { 
        type: 'select', 
        label: 'Bike Model', 
        options: []
      },
      dateRange: {
        type: 'dateRange',
        startLabel: 'From Date',
        endLabel: 'To Date',
        startField: 'pickupDateTime',
        endField: 'dropoffDateTime'
      },
      status: {
        type: 'select',
        label: 'Status',
        options: [
          { value: 'all', label: 'All Statuses' },
          { value: 'booked', label: 'Booked' },
          { value: 'pending', label: 'Pending' },
          { value: 'cancel', label: 'Cancel' }
        ]
      },
      paymentStatus: {
        type: 'select',
        label: 'Payment Status',
        options: [
          { value: 'all', label: 'All Payment Statuses' },
          { value: 'paid', label: 'Paid' },
          { value: 'not paid', label: 'Not Paid' }
        ]
      },
      licenseStatus: {
        type: 'select',
        label: 'License Status',
        options: [
          { value: 'all', label: 'All License Statuses' },
          { value: 'applied', label: 'Applied' },
          { value: 'not applied', label: 'Not Applied' },
          { value: 'pending', label: 'Pending' }
        ]
      }
    },
    ...filterOptions
  };

  // If bikeModel options was provided in props, use those
  if (filterOptions.bikeModels) {
    options.filterFields.bikeModel.options = [
      { value: '', label: 'All Bikes' },
      ...filterOptions.bikeModels.map(bike => ({ value: bike, label: bike }))
    ];
  }

  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    customerName: '',
    whatsappNumber: '',
    licenseNumber: '',
    bikeModel: '',
    dateRange: {
      startDate: '',
      endDate: ''
    },
    status: 'all',
    paymentStatus: 'all',
    licenseStatus: 'all'
  });
  const [filteredData, setFilteredData] = useState([]);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      // Handle nested properties like dateRange.startDate
      const [parent, child] = name.split('.');
      setFilters(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      customerName: '',
      whatsappNumber: '',
      licenseNumber: '',
      bikeModel: '',
      dateRange: {
        startDate: '',
        endDate: ''
      },
      status: 'all',
      paymentStatus: 'all',
      licenseStatus: 'all'
    });
    setSearchQuery('');
  };

  // Helper function to safely access nested Firebase properties
  const getFieldValue = (item, field) => {
    if (!item) return '';
    
    // Handle Firebase Timestamp objects
    if (item[field] && typeof item[field] === 'object' && item[field].seconds) {
      return new Date(item[field].seconds * 1000).toISOString();
    }
    
    // Handle regular string/number values
    return item[field] || '';
  };

  // Helper function to format dates for comparison
  const formatDateForComparison = (dateValue) => {
    if (!dateValue) return null;
    
    try {
      // If it's a Firebase Timestamp
      if (typeof dateValue === 'object' && dateValue.seconds) {
        return new Date(dateValue.seconds * 1000);
      }
      
      // If it's a string
      if (typeof dateValue === 'string') {
        return new Date(dateValue);
      }
      
      // If it's already a Date object
      if (dateValue instanceof Date) {
        return dateValue;
      }
      
      return null;
    } catch (error) {
      console.error('Error parsing date:', dateValue, error);
      return null;
    }
  };

  // Apply filters to data - memoized to prevent unnecessary recalculations
  const applyFilters = useCallback((items) => {
    if (!items || !items.length) return [];
    
    return items.filter(item => {
      // Search query filter (case insensitive)
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        // Check all searchable fields
        const matchesSearch = options.searchFields.some(field => {
          const fieldValue = getFieldValue(item, field);
          return fieldValue && fieldValue.toString().toLowerCase().includes(query);
        });
        
        if (!matchesSearch) return false;
      }
      
      // Text field filters
      if (filters.customerName) {
        const customerName = getFieldValue(item, 'customerName');
        if (!customerName || !customerName.toLowerCase().includes(filters.customerName.toLowerCase())) {
          return false;
        }
      }
      
      // WhatsApp Number filter
      if (filters.whatsappNumber) {
        const whatsappNumber = getFieldValue(item, 'whatsappNumber');
        if (!whatsappNumber || !whatsappNumber.toLowerCase().includes(filters.whatsappNumber.toLowerCase())) {
          return false;
        }
      }
      
      if (filters.licenseNumber) {
        const licenseNumber = getFieldValue(item, 'licenseNumber');
        if (!licenseNumber || !licenseNumber.toLowerCase().includes(filters.licenseNumber.toLowerCase())) {
          return false;
        }
      }
      
      // Select field filters
      if (filters.bikeModel && getFieldValue(item, 'motorbikeNumber') !== filters.bikeModel) {
        return false;
      }
      
      // Date range filters
      if (filters.dateRange.startDate) {
        const startFilterDate = new Date(filters.dateRange.startDate);
        const pickupDate = formatDateForComparison(item.pickupDateTime);
        if (!pickupDate || pickupDate < startFilterDate) return false;
      }
      
      if (filters.dateRange.endDate) {
        const endFilterDate = new Date(filters.dateRange.endDate);
        const dropoffDate = formatDateForComparison(item.dropoffDateTime);
        if (!dropoffDate || dropoffDate > endFilterDate) return false;
      }
      
      // Status filters
      if (filters.status !== 'all' && getFieldValue(item, 'availabilityStatus') !== filters.status) {
        return false;
      }
      
      if (filters.paymentStatus !== 'all' && getFieldValue(item, 'rentalPaymentStatus') !== filters.paymentStatus) {
        return false;
      }
      
      if (filters.licenseStatus !== 'all' && getFieldValue(item, 'drivingLicenseStatus') !== filters.licenseStatus) {
        return false;
      }
      
      // If all filters passed, include this item
      return true;
    });
  }, [searchQuery, filters]);

  // Export data to CSV
  const exportFilteredData = () => {
    // Define CSV headers
    const headers = [
      'Customer Name',
      'WhatsApp Number',
      'Local Number',
      'Email',
      'License Number',
      'Pickup Date',
      'Dropoff Date',
      'Bike Model',
      'Status',
      'Rental Amount',
      'Payment Status',
      'Created Date'
    ].join(',');
    
    // Convert data to CSV rows
    const rows = filteredData.map(item => [
      `"${getFieldValue(item, 'customerName')}"`,
      `"${getFieldValue(item, 'whatsappNumber')}"`,
      `"${getFieldValue(item, 'localNumber')}"`,
      `"${getFieldValue(item, 'email')}"`,
      `"${getFieldValue(item, 'licenseNumber')}"`,
      `"${getFieldValue(item, 'pickupDateTime')}"`,
      `"${getFieldValue(item, 'dropoffDateTime')}"`,
      `"${getFieldValue(item, 'motorbikeNumber')}"`,
      `"${getFieldValue(item, 'availabilityStatus')}"`,
      `"${getFieldValue(item, 'rentalAmount')}"`,
      `"${getFieldValue(item, 'rentalPaymentStatus')}"`,
      `"${item.createdAt ? (item.createdAt.seconds ? new Date(item.createdAt.seconds * 1000).toISOString() : item.createdAt) : ''}"`
    ].join(','));
    
    // Combine headers and rows
    const csvContent = [headers, ...rows].join('\n');
    
    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `firebase-reservations-export-${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Apply filters when data or filters change
  useEffect(() => {
    const filtered = applyFilters(data);
    setFilteredData(filtered);
  }, [data, applyFilters]);

  // Separate effect for notifying parent component
  useEffect(() => {
    if (onFilteredDataChange) {
      onFilteredDataChange(filteredData);
    }
  }, [filteredData]);

  // Check if any filters are active
  const hasActiveFilters = () => {
    return (
      filters.customerName || 
      filters.whatsappNumber || 
      filters.licenseNumber || 
      filters.bikeModel || 
      filters.dateRange.startDate || 
      filters.dateRange.endDate || 
      filters.status !== 'all' || 
      filters.paymentStatus !== 'all' || 
      filters.licenseStatus !== 'all'
    );
  };

  return (
    <div className="mb-6 bg-gray-800 rounded-xl shadow-lg border border-gray-700 overflow-hidden">
      {/* Header Section with Search and Actions */}
      <div className="p-4 border-b border-gray-700 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        {/* Search Bar */}
        <div className="flex items-center gap-2 w-full lg:w-1/3">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, phone, or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-2">
          {/* Filter Toggle Button */}
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            <Filter size={18} />
            Filters
            {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          
          {/* Export Button */}
          {showExport && filteredData.length > 0 && (
            <button 
              onClick={exportFilteredData}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
            >
              <Download size={18} />
              Export CSV
            </button>
          )}
        </div>
      </div>
      
      {/* Firebase Connection Indicator */}
      <div className="px-4 py-2 bg-green-900/20 border-b border-gray-700 text-center">
        <p className="text-green-400 text-xs">
          ✅ Connected to Firebase • Real-time filtering on {data.length} records
        </p>
      </div>
      
      {/* Expandable Filter Options */}
      {showFilters && (
        <div className="p-6 border-b border-gray-700 bg-gray-800 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Customer Name Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Customer Name</label>
            <input
              type="text"
              name="customerName"
              value={filters.customerName}
              onChange={handleFilterChange}
              className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="Filter by name"
            />
          </div>
          
          {/* WhatsApp Number Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">WhatsApp Number</label>
            <input
              type="text"
              name="whatsappNumber"
              value={filters.whatsappNumber}
              onChange={handleFilterChange}
              className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="Filter by WhatsApp"
            />
          </div>
          
          {/* License Number Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">License Number</label>
            <input
              type="text"
              name="licenseNumber"
              value={filters.licenseNumber}
              onChange={handleFilterChange}
              className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="Filter by license"
            />
          </div>
          
          {/* Bike Model Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Bike Model</label>
            <select
              name="bikeModel"
              value={filters.bikeModel}
              onChange={handleFilterChange}
              className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              {options.filterFields.bikeModel.options.map((option, index) => (
                <option key={index} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          
          {/* Date Range Filter - Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">From Date</label>
            <input
              type="date"
              name="dateRange.startDate"
              value={filters.dateRange.startDate}
              onChange={handleFilterChange}
              className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
          
          {/* Date Range Filter - End Date */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">To Date</label>
            <input
              type="date"
              name="dateRange.endDate"
              value={filters.dateRange.endDate}
              onChange={handleFilterChange}
              className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
          
          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Status</label>
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              {options.filterFields.status.options.map((option, index) => (
                <option key={index} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          
          {/* Payment Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Payment Status</label>
            <select
              name="paymentStatus"
              value={filters.paymentStatus}
              onChange={handleFilterChange}
              className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              {options.filterFields.paymentStatus.options.map((option, index) => (
                <option key={index} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          
          {/* License Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">License Status</label>
            <select
              name="licenseStatus"
              value={filters.licenseStatus}
              onChange={handleFilterChange}
              className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              {options.filterFields.licenseStatus.options.map((option, index) => (
                <option key={index} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          
          {/* Reset Filters Button */}
          <div className="flex items-end">
            <button
              onClick={resetFilters}
              className="w-full p-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-lg transition-colors border border-purple-700 shadow-md font-medium flex items-center justify-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Reset Filters
            </button>
          </div>
        </div>
      )}
      
      {/* Active Filters Display */}
      {hasActiveFilters() && (
        <div className="p-4 bg-gray-750 border-t border-gray-700 flex flex-wrap gap-2">
          <span className="text-sm text-gray-400">Active filters:</span>
          
          {filters.customerName && (
            <span className="px-2 py-1 bg-gray-700 text-gray-300 rounded-lg text-xs flex items-center gap-1">
              <CheckSquare size={12} className="text-orange-400" />
              Name: {filters.customerName}
            </span>
          )}
          
          {filters.whatsappNumber && (
            <span className="px-2 py-1 bg-gray-700 text-gray-300 rounded-lg text-xs flex items-center gap-1">
              <CheckSquare size={12} className="text-orange-400" />
              WhatsApp: {filters.whatsappNumber}
            </span>
          )}
          
          {filters.licenseNumber && (
            <span className="px-2 py-1 bg-gray-700 text-gray-300 rounded-lg text-xs flex items-center gap-1">
              <CheckSquare size={12} className="text-orange-400" />
              License: {filters.licenseNumber}
            </span>
          )}
          
          {filters.bikeModel && (
            <span className="px-2 py-1 bg-gray-700 text-gray-300 rounded-lg text-xs flex items-center gap-1">
              <CheckSquare size={12} className="text-orange-400" />
              Bike: {filters.bikeModel}
            </span>
          )}
          
          {(filters.dateRange.startDate || filters.dateRange.endDate) && (
            <span className="px-2 py-1 bg-gray-700 text-gray-300 rounded-lg text-xs flex items-center gap-1">
              <CheckSquare size={12} className="text-orange-400" />
              Date: {filters.dateRange.startDate || 'Any'} to {filters.dateRange.endDate || 'Any'}
            </span>
          )}
          
          {filters.status !== 'all' && (
            <span className="px-2 py-1 bg-gray-700 text-gray-300 rounded-lg text-xs flex items-center gap-1">
              <CheckSquare size={12} className="text-orange-400" />
              Status: {filters.status}
            </span>
          )}
          
          {filters.paymentStatus !== 'all' && (
            <span className="px-2 py-1 bg-gray-700 text-gray-300 rounded-lg text-xs flex items-center gap-1">
              <CheckSquare size={12} className="text-orange-400" />
              Payment: {filters.paymentStatus}
            </span>
          )}
          
          {filters.licenseStatus !== 'all' && (
            <span className="px-2 py-1 bg-gray-700 text-gray-300 rounded-lg text-xs flex items-center gap-1">
              <CheckSquare size={12} className="text-orange-400" />
              License Status: {filters.licenseStatus}
            </span>
          )}
        </div>
      )}
      
      {/* Filter Stats */}
      {data && data.length > 0 && (
        <div className="p-4 bg-gray-700 border-t border-gray-600">
          <div className="text-sm text-gray-300">
            {data.length === filteredData.length ? (
              <span>Showing all {data.length} records from Firebase</span>
            ) : (
              <span>
                Showing <span className="text-orange-400 font-medium">{filteredData.length}</span> of {data.length} records from Firebase
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterSystem;